import type { ReactNode } from 'react'
import { Skeleton } from './Skeleton'

export interface SegmentedControlOption {
  readonly value: string
  readonly label: string
  /** Optional leading icon, rendered before the label (decorative). */
  readonly icon?: ReactNode
  readonly disabled?: boolean
  /** Always-visible caption shown under the control when this option is
   * disabled (UX spec §2.3.2/§4.3 — "disabled-with-generic-reason, not
   * hidden," discoverable via an adjacent caption rather than requiring
   * focus/hover to reach the disabled option itself). */
  readonly disabledReason?: string
}

export interface SegmentedControlProps {
  /** Accessible group name — rendered as the `<fieldset>`'s `<legend>`. */
  readonly legend: string
  readonly name: string
  readonly options: readonly SegmentedControlOption[]
  readonly value: string
  readonly onChange: (value: string) => void
  /** Skeleton-shimmer state while eligibility resolves (UX spec §2.3.2) —
   * NOT a blocking spinner; other steps stay interactive while this loads. */
  readonly loading?: boolean
}

/**
 * A 2-3 way exclusive-choice control (components.md "Toggle / segmented
 * control"), built the same way as RadioCard: native radio inputs in a
 * `<fieldset>`/`<legend>`, styled as adjoining segments. This is a radio
 * GROUP (one exclusive choice from a small fixed set), not a tablist — using
 * native radios avoids hand-rolling `role="radiogroup"`/arrow-key handling.
 * First consumer: UID-60 checkout's Card/ACH/PO payment-method choice.
 */
export function SegmentedControl({
  legend,
  name,
  options,
  value,
  onChange,
  loading = false,
}: SegmentedControlProps) {
  if (loading) {
    return (
      <fieldset className="segmented-control-fieldset">
        <legend className="sr-only">{legend}</legend>
        <div className="segmented-control" aria-hidden="true">
          {options.map((option) => (
            <Skeleton key={option.value} height={32} />
          ))}
        </div>
        <p className="sr-only" aria-live="polite">
          Checking payment method availability…
        </p>
      </fieldset>
    )
  }

  const disabledCaptions = options.filter((option) => option.disabled && option.disabledReason)

  return (
    <fieldset className="segmented-control-fieldset">
      <legend className="segmented-control-legend">{legend}</legend>
      <div className="segmented-control">
        {options.map((option) => {
          const active = option.value === value
          return (
            <label
              key={option.value}
              className={[
                'segmented-control-segment',
                active && 'segmented-control-segment-active',
                option.disabled && 'segmented-control-segment-disabled',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={active}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                className="segmented-control-input"
              />
              {option.icon != null && (
                <span className="segmented-control-segment-icon" aria-hidden="true">
                  {option.icon}
                </span>
              )}
              {option.label}
            </label>
          )
        })}
      </div>
      {disabledCaptions.map((option) => (
        <p key={option.value} className="segmented-control-disabled-caption">
          <strong>{option.label}</strong> — {option.disabledReason}
        </p>
      ))}
    </fieldset>
  )
}
